import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiArrowLeft,
  FiAward,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiEdit2,
  FiFileText,
  FiMapPin,
  FiPhone,
  FiShield,
  FiTrendingUp,
  FiUser,
  FiLock,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, EMPLOYEE_SALARY_VIEW_PERMISSION } from "../../utils/permissions";
import { BiRupee } from 'react-icons/bi';
import SensitiveEmployeeDetailsModal from "./components/SensitiveEmployeeDetailsModal";

export default function EmployeeDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken, user } = useAuth();

  const currentYear = new Date().getFullYear();

  const [employee, setEmployee] = useState(null);
  const [commissionSummary, setCommissionSummary] = useState(null);
  const [commissionYear, setCommissionYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showSensitiveDetails, setShowSensitiveDetails] = useState(false);

  const loadEmployee = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [employeeResponse, commissionResponse] = await Promise.all([
        apiRequest(`/employees/${id}`, { accessToken }),
        apiRequest(`/employees/${id}/commissions?year=${commissionYear}`, {
          accessToken,
        }),
      ]);

      setEmployee(employeeResponse);
      setCommissionSummary(commissionResponse);
    } catch (requestError) {
      setError(requestError.message || "Could not load employee details.");
      setEmployee(null);
      setCommissionSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, commissionYear, id]);

  useEffect(() => {
    loadEmployee();
  }, [loadEmployee]);

  const primaryAssignment = useMemo(
    () =>
      employee?.assignments?.find((assignment) => assignment.isPrimary) ||
      employee?.assignments?.[0] ||
      null,
    [employee],
  );

  const erpRoles = useMemo(
    () =>
      (employee?.user?.roles || [])
        .map((userRole) => userRole.role || userRole)
        .filter((role) => role?.code && role.code !== "EMPLOYEE_SELF_SERVICE"),
    [employee],
  );

  const activeCommissionRules = useMemo(
    () => (employee?.commissionRules || []).filter((rule) => rule.isActive),
    [employee],
  );

  const commissionPercent = useMemo(() => {
    const defaultRule = activeCommissionRules.find(
      (rule) =>
        rule.name === "Default POS Commission" &&
        rule.scope === "ALL_PRODUCTS" &&
        rule.rateType === "PERCENT",
    );

    return defaultRule?.rate ?? null;
  }, [activeCommissionRules]);

  const attendanceSummary = useMemo(() => {
    const records = employee?.attendanceRecords || [];

    return records.reduce(
      (summary, record) => {
        summary.total += 1;

        if (record.status === "PRESENT") summary.present += 1;
        if (record.status === "LATE") summary.late += 1;
        if (record.status === "ABSENT") summary.absent += 1;
        if (record.status === "ON_LEAVE") summary.leave += 1;

        return summary;
      },
      { total: 0, present: 0, late: 0, absent: 0, leave: 0 },
    );
  }, [employee]);

  if (isLoading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center text-primary-700">
        <FiClock className="mr-3 animate-spin" size={24} />
        Loading employee details...
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="rounded-3xl border border-red-100 bg-white p-8">
        <p className="text-red-700">{error || "Employee not found."}</p>

        <button
          type="button"
          onClick={() => navigate("/admindashboard/employees")}
          className="mt-5 h-11 rounded-xl bg-primary-700 px-5 text-white"
        >
          Back to Employees
        </button>
      </div>
    );
  }

  const fullName = employee.fullName || "Employee";
  const nickname = employee.nickname?.trim() || "";
  const employeeCode = employee.employeeCode || "—";

  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .map((name) => name[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const branchName = primaryAssignment?.branch?.name || "Not assigned";

  const departmentName = primaryAssignment?.department?.name || "Not assigned";

  const designationName =
    primaryAssignment?.designation?.name || "Not assigned";

  const canViewSensitiveDocuments = hasPermission(
    user,
    "employees.view_sensitive_documents",
  );
  const canViewBankDetails = hasPermission(user, "employees.view_bank_details");
  const canViewSalary = hasPermission(user, EMPLOYEE_SALARY_VIEW_PERMISSION) && employee.salaryVisibility !== 'PRIVATE';
  const canViewSensitiveDetails = canViewSensitiveDocuments || canViewBankDetails;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate("/admindashboard/employees")}
            className="mb-4 flex items-center gap-2 text-gray-500 hover:text-primary-700"
          >
            <FiArrowLeft />
            Back to Employees
          </button>

          <h1 className="text-4xl font-bold text-gray-900">Employee Details</h1>

          <p className="mt-2 text-gray-500">
            Complete employee profile, HR records, payroll, attendance,
            commissions and POS performance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          {canViewSensitiveDetails && (
            <button
              type="button"
              onClick={() => setShowSensitiveDetails(true)}
              className="flex h-11 items-center gap-2 rounded-xl border border-primary-200 bg-white px-5 font-medium text-primary-800 hover:bg-primary-50"
            >
              <FiLock />
              View secure details
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              navigate(`/admindashboard/employees/${employee.id}/edit`)
            }
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800"
          >
            <FiEdit2 />
            Edit Employee
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700">
          {error}
        </div>
      ) : null}

      <section className="relative overflow-hidden rounded-3xl bg-primary-700 p-8 text-white">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="absolute -bottom-20 right-24 h-48 w-48 rounded-full bg-white/5" />

        <div className="relative flex flex-col gap-6 md:flex-row md:items-center">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl border border-white/20 bg-white/15 text-3xl font-bold">
            {initials || "EM"}
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-3xl font-bold">
                {fullName}
                {nickname ? ` (${nickname})` : ""}
              </h2>
              <StatusBadge status={employee.status} />
            </div>

            <p className="mt-2 text-white/80">
              {designationName} · {departmentName}
            </p>

            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              <span>Employee Code: {employeeCode}</span>
              <span>Branch: {branchName}</span>
              <span>Joined: {formatDate(employee.joiningDate)}</span>
              <span>
                Last Login:{" "}
                {employee.user?.lastLoginAt
                  ? formatDateTime(employee.user.lastLoginAt)
                  : "Never"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Basic Salary"
          value={canViewSalary ? formatMoney(employee.compensation?.basicSalary) : 'Private'}
          icon={<BiRupee size={22} />}
          color="bg-green-50 text-green-700"
        />

        <StatCard
          title="Salary Type"
          value={canViewSalary ? toTitle(employee.compensation?.salaryType) : 'Private'}
          icon={<FiCreditCard size={22} />}
          color="bg-blue-50 text-blue-700"
        />

        <StatCard
          title="ERP Access"
          value={erpRoles.length ? `${erpRoles.length} Role(s)` : "Mobile Only"}
          icon={<FiShield size={22} />}
          color="bg-purple-50 text-purple-700"
        />

        <StatCard
          title="Default Commission"
          value={
            commissionPercent !== null
              ? `${Number(commissionPercent)}%`
              : "Not configured"
          }
          icon={<FiAward size={22} />}
          color="bg-orange-50 text-orange-700"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <InfoPanel
          title="Personal Information"
          icon={<FiUser size={22} />}
          iconClassName="bg-primary-50 text-primary-700"
          rows={[
            ["Full Name", employee.fullName],
            ["Nickname", employee.nickname || "—"],
            ["Gender", toTitle(employee.gender)],
            ["Date of Birth", formatDate(employee.dateOfBirth)],
            ["Employee Status", toTitle(employee.status)],
            ["Profile Created", formatDateTime(employee.createdAt)],
            ["Last Updated", formatDateTime(employee.updatedAt)],
          ]}
        />

        <InfoPanel
          title="Contact & Account"
          icon={<FiPhone size={22} />}
          iconClassName="bg-green-50 text-green-700"
          rows={[
            ["Email Address", employee.user?.email],
            ["Phone Number", employee.user?.phone],
            ["Account Status", toTitle(employee.user?.status)],
            [
              "Last Login",
              employee.user?.lastLoginAt
                ? formatDateTime(employee.user.lastLoginAt)
                : "Never",
            ],
          ]}
        />
      </div>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiBriefcase size={22} />}
          iconClassName="bg-blue-50 text-blue-700"
          title="Employment Assignment"
          subtitle="Current and historical department, designation and branch assignments."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Branch</th>
                <th className="px-6 py-4">Department</th>
                <th className="px-6 py-4">Designation</th>
                <th className="px-6 py-4">Employment Type</th>
                <th className="px-6 py-4">Started</th>
                <th className="px-6 py-4">Ended</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.assignments || []).map((assignment) => (
                <tr key={assignment.id}>
                  <td className="px-6 py-4 font-medium">
                    {assignment.branch?.name || "—"}
                  </td>
                  <td className="px-6 py-4">
                    {assignment.department?.name || "—"}
                  </td>
                  <td className="px-6 py-4">
                    {assignment.designation?.name || "—"}
                  </td>
                  <td className="px-6 py-4">
                    {toTitle(assignment.employmentType)}
                  </td>
                  <td className="px-6 py-4">
                    {formatDate(assignment.startedOn)}
                  </td>
                  <td className="px-6 py-4">
                    {formatDate(assignment.endedOn)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        assignment.isPrimary
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {assignment.isPrimary ? "Primary" : "Previous"}
                    </span>
                  </td>
                </tr>
              ))}

              {!employee.assignments?.length ? (
                <EmptyTableRow
                  columns={7}
                  text="No employment assignments found."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <InfoPanel
          title="Compensation & Payroll Settings"
          icon={<BiRupee size={22} />}
          iconClassName="bg-green-50 text-green-700"
          rows={canViewSalary ? [
            ["Salary Type", toTitle(employee.compensation?.salaryType)],
            ["Basic Salary", formatMoney(employee.compensation?.basicSalary)],
            ...(employee.compensation?.ctc != null ? [["Annual CTC", formatMoney(employee.compensation.ctc)]] : []),
            [
              "Overtime Eligible",
              yesNo(employee.compensation?.overtimeEligible),
            ],
            [
              "Configured Overtime Rate",
              employee.compensation?.overtimeRate
                ? `${formatMoney(employee.compensation.overtimeRate)} / hour`
                : "Auto-calculated from shift",
            ],
            [
              "Early Exit Deduction",
              yesNo(employee.compensation?.earlyExitDeductionEligible),
            ],
            [
              "Configured Early Exit Rate",
              employee.compensation?.earlyExitDeductionRate
                ? `${formatMoney(
                    employee.compensation.earlyExitDeductionRate,
                  )} / hour`
                : "Auto-calculated from shift",
            ],
            ["Payment Mode", toTitle(employee.compensation?.paymentMode)],
          ] : [["Salary details", "Private"]]}
        />

        <InfoPanel
          title="Work Schedule & Holiday Settings"
          icon={<FiClock size={22} />}
          iconClassName="bg-orange-50 text-orange-700"
          rows={[
            ["Shift Type", employee.workSchedule?.shiftType],
            [
              "Shift Timing",
              employee.workSchedule?.shiftStartTime &&
              employee.workSchedule?.shiftEndTime
                ? `${employee.workSchedule.shiftStartTime} – ${employee.workSchedule.shiftEndTime}`
                : "—",
            ],
            [
              "Break Duration",
              employee.workSchedule?.breakDurationMinutes !== null &&
              employee.workSchedule?.breakDurationMinutes !== undefined
                ? `${employee.workSchedule.breakDurationMinutes} minutes`
                : "—",
            ],
            [
              "Grace Time",
              employee.workSchedule?.graceMinutes !== null &&
              employee.workSchedule?.graceMinutes !== undefined
                ? `${employee.workSchedule.graceMinutes} minutes`
                : "—",
            ],
            [
              "Monthly Holiday Allowance",
              `${employee.workSchedule?.monthlyHolidayCount || 0} day(s)`,
            ],
          ]}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-7">
          <SectionHeader
            compact
            icon={<FiCreditCard size={22} />}
            iconClassName="bg-indigo-50 text-indigo-700"
            title="Bank Account"
            subtitle="Only masked banking information is shown."
          />

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <DetailBlock
              label="Bank Name"
              value={employee.bankAccount?.bankName}
            />
            <DetailBlock
              label="Account Holder"
              value={employee.bankAccount?.accountHolderName}
            />
            <DetailBlock
              label="Account Number"
              value={
                employee.bankAccount?.accountNumberLast4
                  ? `•••• •••• ${employee.bankAccount.accountNumberLast4}`
                  : "—"
              }
            />
            <DetailBlock
              label="Payment Mode"
              value={canViewSalary ? toTitle(employee.compensation?.paymentMode) : 'Private'}
            />
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-7">
          <SectionHeader
            compact
            icon={<FiFileText size={22} />}
            iconClassName="bg-rose-50 text-rose-700"
            title="Identity Documents"
            subtitle="Sensitive document values remain masked."
          />

          <div className="mt-5 space-y-3">
            {(employee.documents || []).map((document) => (
              <div
                key={document.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gray-50 p-4"
              >
                <div>
                  <p className="font-semibold text-gray-900">
                    {toTitle(document.documentType)}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {document.documentNumberLast4
                      ? `•••• ${document.documentNumberLast4}`
                      : document.originalName || "Document number unavailable"}
                  </p>
                </div>

                <StatusBadge status={document.status} />
              </div>
            ))}

            {!employee.documents?.length ? (
              <EmptyState text="No identity documents have been added." />
            ) : null}
          </div>
        </section>
      </div>

      <section className="rounded-3xl border border-gray-100 bg-white p-7">
        <SectionHeader
          compact
          icon={<FiShield size={22} />}
          iconClassName="bg-purple-50 text-purple-700"
          title="System Access"
          subtitle="ERP roles and active branch access for this employee."
        />

        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          <div>
            <p className="mb-3 text-sm font-semibold text-gray-700">
              ERP Roles
            </p>

            {erpRoles.length ? (
              <div className="flex flex-wrap gap-3">
                {erpRoles.map((role) => (
                  <div
                    key={role.id || role.code}
                    className="rounded-2xl border border-primary-100 bg-primary-50 px-4 py-3"
                  >
                    <p className="font-semibold text-primary-800">
                      {role.name || role.code}
                    </p>
                    <p className="mt-1 text-xs text-primary-700">{role.code}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="ERP dashboard access is not enabled." />
            )}
          </div>

          <div>
            <p className="mb-3 text-sm font-semibold text-gray-700">
              Active Branch Access
            </p>

            {employee.user?.branchAccess?.length ? (
              <div className="space-y-3">
                {employee.user.branchAccess.map((access) => (
                  <div
                    key={access.branch?.id}
                    className="flex items-center justify-between rounded-2xl bg-gray-50 p-4"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">
                        {access.branch?.name || "—"}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {access.branch?.code || "—"}
                      </p>
                    </div>

                    {access.isDefault ? (
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                        Default
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No active ERP branch access." />
            )}
          </div>
        </div>
      </section>

      {showSensitiveDetails && (
        <SensitiveEmployeeDetailsModal
          employee={employee}
          accessToken={accessToken}
          canViewDocuments={canViewSensitiveDocuments}
          canViewBank={canViewBankDetails}
          onClose={() => setShowSensitiveDetails(false)}
        />
      )}

      <CommissionSummary
        summary={commissionSummary}
        selectedYear={commissionYear}
        onYearChange={setCommissionYear}
        commissionPercent={commissionPercent}
      />

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiAward size={22} />}
          iconClassName="bg-yellow-50 text-yellow-700"
          title="Commission Rules"
          subtitle="Configured commission eligibility and rates."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Rule</th>
                <th className="px-6 py-4">Scope</th>
                <th className="px-6 py-4">Rate</th>
                <th className="px-6 py-4">Applies To</th>
                <th className="px-6 py-4">Period</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.commissionRules || []).map((rule) => (
                <tr key={rule.id}>
                  <td className="px-6 py-4 font-medium">{rule.name}</td>
                  <td className="px-6 py-4">{toTitle(rule.scope)}</td>
                  <td className="px-6 py-4">
                    {rule.rateType === "PERCENT"
                      ? `${Number(rule.rate)}%`
                      : formatMoney(rule.rate)}
                  </td>
                  <td className="px-6 py-4">
                    {rule.variant?.sku ||
                      rule.product?.name ||
                      "All eligible products"}
                  </td>
                  <td className="px-6 py-4">
                    {formatDate(rule.startsAt)}
                    {rule.endsAt ? ` – ${formatDate(rule.endsAt)}` : " onwards"}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge
                      status={rule.isActive ? "ACTIVE" : "INACTIVE"}
                    />
                  </td>
                </tr>
              ))}

              {!employee.commissionRules?.length ? (
                <EmptyTableRow
                  columns={6}
                  text="No commission rules configured."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiTrendingUp size={22} />}
          iconClassName="bg-green-50 text-green-700"
          title="Recent Commission Transactions"
          subtitle="Latest POS commission earnings and return reversals."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Receipt</th>
                <th className="px-6 py-4">Product</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Commissionable Value</th>
                <th className="px-6 py-4">Commission</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.commissionTransactions || []).map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4">
                    {formatDateTime(transaction.occurredAt)}
                  </td>
                  <td className="px-6 py-4 font-medium">
                    {transaction.sale?.receiptNumber || "—"}
                  </td>
                  <td className="px-6 py-4">
                    <p>{transaction.saleItem?.productNameSnapshot || "—"}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {transaction.saleItem?.skuSnapshot || "—"}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={transaction.type} />
                  </td>
                  <td className="px-6 py-4">
                    {formatMoney(transaction.commissionableAmount)}
                  </td>
                  <td
                    className={`px-6 py-4 font-bold ${
                      transaction.type === "REVERSED"
                        ? "text-red-700"
                        : "text-green-700"
                    }`}
                  >
                    {transaction.type === "REVERSED" ? "-" : "+"}
                    {formatMoney(transaction.commissionAmount)}
                  </td>
                </tr>
              ))}

              {!employee.commissionTransactions?.length ? (
                <EmptyTableRow
                  columns={6}
                  text="No commission transactions found."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiActivity size={22} />}
          iconClassName="bg-cyan-50 text-cyan-700"
          title="POS Sales Performance"
          subtitle="Most recent sale items where this employee was selected as the sales agent."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Receipt</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Product</th>
                <th className="px-6 py-4">Store</th>
                <th className="px-6 py-4">Quantity</th>
                <th className="px-6 py-4">Line Total</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.posSaleItemsAsAgent || []).map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 font-medium">
                    {item.sale?.receiptNumber || "—"}
                  </td>
                  <td className="px-6 py-4">
                    {formatDateTime(item.sale?.completedAt || item.createdAt)}
                  </td>
                  <td className="px-6 py-4">
                    {item.sale?.customer?.fullName || "Walk-in Customer"}
                  </td>
                  <td className="px-6 py-4">
                    <p>{item.productNameSnapshot}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {item.variantNameSnapshot || item.skuSnapshot}
                    </p>
                  </td>
                  <td className="px-6 py-4">{item.sale?.store?.name || "—"}</td>
                  <td className="px-6 py-4">{Number(item.quantity || 0)}</td>
                  <td className="px-6 py-4 font-semibold">
                    {formatMoney(item.lineTotalAmount)}
                  </td>
                </tr>
              ))}

              {!employee.posSaleItemsAsAgent?.length ? (
                <EmptyTableRow
                  columns={7}
                  text="No POS sales recorded for this employee."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiClock size={22} />}
          iconClassName="bg-blue-50 text-blue-700"
          title="Recent Attendance"
          subtitle={`Latest attendance entries — ${attendanceSummary.present} present, ${attendanceSummary.late} late, ${attendanceSummary.absent} absent.`}
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Check In</th>
                <th className="px-6 py-4">Check Out</th>
                <th className="px-6 py-4">Working Time</th>
                <th className="px-6 py-4">Late / Early Exit</th>
                <th className="px-6 py-4">Source</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.attendanceRecords || []).map((record) => (
                <tr key={record.id}>
                  <td className="px-6 py-4">
                    {formatDate(record.attendanceDate)}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={record.status} />
                  </td>
                  <td className="px-6 py-4">{formatTime(record.checkInAt)}</td>
                  <td className="px-6 py-4">{formatTime(record.checkOutAt)}</td>
                  <td className="px-6 py-4">
                    {formatMinutes(record.workingMinutes)}
                  </td>
                  <td className="px-6 py-4">
                    {record.lateMinutes || 0} min late ·{" "}
                    {record.earlyExitMinutes || 0} min early exit
                  </td>
                  <td className="px-6 py-4">{toTitle(record.source)}</td>
                </tr>
              ))}

              {!employee.attendanceRecords?.length ? (
                <EmptyTableRow
                  columns={7}
                  text="No attendance records found."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-7">
          <SectionHeader
            compact
            icon={<FiCalendar size={22} />}
            iconClassName="bg-green-50 text-green-700"
            title={`Leave Balances · ${currentYear}`}
            subtitle="Current leave allocation and utilisation."
          />

          <div className="mt-5 space-y-3">
            {(employee.leaveBalances || []).map((balance) => {
              const remaining =
                Number(balance.allocatedDays || 0) -
                Number(balance.usedDays || 0);

              return (
                <div
                  key={`${balance.leaveType?.code}-${balance.year}`}
                  className="rounded-2xl bg-gray-50 p-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-semibold">{balance.leaveType?.name}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {balance.leaveType?.isPaid
                          ? "Paid leave"
                          : "Unpaid leave"}
                      </p>
                    </div>

                    <p className="text-lg font-bold text-primary-800">
                      {remaining.toFixed(1)} left
                    </p>
                  </div>

                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-primary-700"
                      style={{
                        width: `${Math.min(
                          (Number(balance.usedDays || 0) /
                            Math.max(Number(balance.allocatedDays || 0), 1)) *
                            100,
                          100,
                        )}%`,
                      }}
                    />
                  </div>

                  <p className="mt-2 text-xs text-gray-500">
                    Used {Number(balance.usedDays || 0)} of{" "}
                    {Number(balance.allocatedDays || 0)} days
                  </p>
                </div>
              );
            })}

            {!employee.leaveBalances?.length ? (
              <EmptyState
                text={`No leave balances exist for ${currentYear}.`}
              />
            ) : null}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-7">
          <SectionHeader
            compact
            icon={<FiFileText size={22} />}
            iconClassName="bg-orange-50 text-orange-700"
            title="Recent Leave Requests"
            subtitle="Latest leave applications and approval status."
          />

          <div className="mt-5 space-y-3">
            {(employee.leaveRequests || []).map((leave) => (
              <div key={leave.id} className="rounded-2xl bg-gray-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {leave.leaveType?.name || "Leave"}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {formatDate(leave.startDate)} –{" "}
                      {formatDate(leave.endDate)} · {Number(leave.totalDays)}{" "}
                      day(s)
                    </p>
                  </div>

                  <StatusBadge status={leave.status} />
                </div>

                {leave.reason ? (
                  <p className="mt-3 text-sm text-gray-600">{leave.reason}</p>
                ) : null}
              </div>
            ))}

            {!employee.leaveRequests?.length ? (
              <EmptyState text="No leave requests found." />
            ) : null}
          </div>
        </section>
      </div>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<BiRupee size={22} />}
          iconClassName="bg-emerald-50 text-emerald-700"
          title="Payroll History"
          subtitle="Generated payroll records, deductions, earnings and payment status."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Period</th>
                <th className="px-6 py-4">Run Type</th>
                <th className="px-6 py-4">Earnings</th>
                <th className="px-6 py-4">Deductions</th>
                <th className="px-6 py-4">Net Payable</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Paid On</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(canViewSalary ? employee.payrollItems || [] : []).map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4">
                    <p className="font-medium">
                      {formatDate(item.payrollRun?.periodStart)} –{" "}
                      {formatDate(item.payrollRun?.periodEnd)}
                    </p>
                    {item.payrollRun?.offCycleReason ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {item.payrollRun.offCycleReason}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-6 py-4">
                    {toTitle(item.payrollRun?.runType)}
                  </td>
                  <td className="px-6 py-4">
                    {formatMoney(item.grossEarnings)}
                  </td>
                  <td className="px-6 py-4 text-red-700">
                    -{formatMoney(item.totalDeductions)}
                  </td>
                  <td className="px-6 py-4 font-bold text-primary-800">
                    {formatMoney(item.netPayable)}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-6 py-4">{formatDate(item.paidAt)}</td>
                </tr>
              ))}

              {!canViewSalary || !employee.payrollItems?.length ? (
                <EmptyTableRow columns={7} text={canViewSalary ? 'No payroll records found.' : 'Private — salary permission required.'} />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <SectionHeader
          icon={<FiActivity size={22} />}
          iconClassName="bg-primary-50 text-primary-700"
          title="Attendance Correction Requests"
          subtitle="Recent correction requests submitted by this employee."
        />

        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-4">Attendance Date</th>
                <th className="px-6 py-4">Requested Status</th>
                <th className="px-6 py-4">Requested Time</th>
                <th className="px-6 py-4">Reason</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Reviewed On</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {(employee.attendanceCorrectionRequests || []).map((request) => (
                <tr key={request.id}>
                  <td className="px-6 py-4">
                    {formatDate(request.attendanceDate)}
                  </td>
                  <td className="px-6 py-4">
                    {toTitle(request.requestedStatus)}
                  </td>
                  <td className="px-6 py-4">
                    {formatTime(request.requestedCheckInAt)} –{" "}
                    {formatTime(request.requestedCheckOutAt)}
                  </td>
                  <td className="max-w-sm px-6 py-4 text-gray-600">
                    {request.reason}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={request.status} />
                  </td>
                  <td className="px-6 py-4">
                    {formatDateTime(request.reviewedAt)}
                  </td>
                </tr>
              ))}

              {!employee.attendanceCorrectionRequests?.length ? (
                <EmptyTableRow
                  columns={6}
                  text="No attendance correction requests found."
                />
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function CommissionSummary({
  summary,
  selectedYear,
  onYearChange,
  commissionPercent,
}) {
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear].sort(
    (first, second) => second - first,
  );

  const months = summary?.months || [];

  return (
    <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
      <SectionHeader
        icon={<FiTrendingUp size={22} />}
        iconClassName="bg-green-50 text-green-700"
        title="Monthly POS Commission"
        subtitle="Monthly net commission after return reversals."
        action={
          <div className="flex items-center gap-3">
            <span className="rounded-xl bg-primary-50 px-3 py-2 text-sm font-semibold text-primary-700">
              {commissionPercent !== null
                ? `Default: ${Number(commissionPercent)}%`
                : "No default rate"}
            </span>

            <select
              value={selectedYear}
              onChange={(event) => onYearChange(Number(event.target.value))}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-primary-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="grid gap-5 border-b border-gray-100 p-7 md:grid-cols-3">
        <CommissionTotalCard
          title="Commission Earned"
          value={summary?.totalEarnedAmount || 0}
          className="bg-green-50 text-green-700"
        />
        <CommissionTotalCard
          title="Commission Reversed"
          value={summary?.totalReversedAmount || 0}
          className="bg-red-50 text-red-700"
        />
        <CommissionTotalCard
          title="Net Commission"
          value={summary?.totalNetAmount || 0}
          className="bg-primary-50 text-primary-700"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 p-7 md:grid-cols-3 xl:grid-cols-4">
        {months.map((month) => (
          <div
            key={month.month}
            className="rounded-2xl border border-gray-100 bg-gray-50 p-5"
          >
            <div className="flex items-center justify-between">
              <p className="font-semibold">{month.label}</p>
              <FiTrendingUp
                className={
                  month.netAmount >= 0
                    ? "text-green-600"
                    : "rotate-180 text-red-600"
                }
              />
            </div>

            <p className="mt-3 text-xl font-bold text-primary-800">
              {formatMoney(month.netAmount)}
            </p>

            <p className="mt-2 text-xs text-gray-500">
              Earned {formatMoney(month.earnedAmount)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Reversed {formatMoney(month.reversedAmount)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({
  icon,
  iconClassName,
  title,
  subtitle,
  action,
  compact = false,
}) {
  return (
    <div
      className={`${compact ? "" : "border-b border-gray-100"} flex flex-col gap-4 p-7 lg:flex-row lg:items-center lg:justify-between`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${iconClassName}`}
        >
          {icon}
        </div>

        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
          ) : null}
        </div>
      </div>

      {action}
    </div>
  );
}

function InfoPanel({ title, icon, iconClassName, rows }) {
  return (
    <section className="rounded-3xl border border-gray-100 bg-white p-7">
      <SectionHeader
        compact
        title={title}
        icon={icon}
        iconClassName={iconClassName}
      />

      <div className="mt-5">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex justify-between gap-5 border-b border-gray-100 py-4 last:border-b-0"
          >
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">
              {value || "—"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function StatCard({ title, value, icon, color }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <p className="mt-3 truncate text-xl font-bold text-gray-900">
            {value || "—"}
          </p>
        </div>

        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ label, value }) {
  return (
    <div className="rounded-2xl bg-gray-50 p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-2 font-semibold text-gray-900">{value || "—"}</p>
    </div>
  );
}

function CommissionTotalCard({ title, value, className }) {
  return (
    <div className={`rounded-2xl p-5 ${className}`}>
      <p className="text-sm font-medium opacity-80">{title}</p>
      <p className="mt-2 text-3xl font-bold">{formatMoney(value)}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl bg-gray-50 p-5 text-center text-sm text-gray-500">
      {text}
    </div>
  );
}

function EmptyTableRow({ columns, text }) {
  return (
    <tr>
      <td
        colSpan={columns}
        className="px-6 py-10 text-center text-sm text-gray-500"
      >
        {text}
      </td>
    </tr>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ACTIVE: "bg-green-100 text-green-700",
    APPROVED: "bg-green-100 text-green-700",
    COMPLETED: "bg-green-100 text-green-700",
    PAID: "bg-green-100 text-green-700",
    POSTED: "bg-green-100 text-green-700",
    PRESENT: "bg-green-100 text-green-700",
    EARNED: "bg-green-100 text-green-700",
    PENDING: "bg-yellow-100 text-yellow-700",
    DRAFT: "bg-yellow-100 text-yellow-700",
    LATE: "bg-orange-100 text-orange-700",
    ON_LEAVE: "bg-blue-100 text-blue-700",
    REVERSED: "bg-red-100 text-red-700",
    REJECTED: "bg-red-100 text-red-700",
    CANCELLED: "bg-red-100 text-red-700",
    ABSENT: "bg-red-100 text-red-700",
    EXITED: "bg-red-100 text-red-700",
    INACTIVE: "bg-gray-200 text-gray-700",
    VOIDED: "bg-gray-200 text-gray-700",
  };

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${styles[status] || "bg-gray-100 text-gray-700"}`}
    >
      {toTitle(status)}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMinutes(value) {
  if (value === null || value === undefined) return "—";

  const hours = Math.floor(Number(value) / 60);
  const minutes = Number(value) % 60;

  return `${hours}h ${minutes}m`;
}

function yesNo(value) {
  if (value === null || value === undefined) return "Not configured";
  return value ? "Yes" : "No";
}

function toTitle(value = "") {
  if (!value) return "—";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
